import { QueryClient, QueryFilters } from '@tanstack/react-query';
import { debounce, DebouncedFunc } from 'lodash-es';

import { reReadPatchedQuery } from '~hooks/useMessaging/reReadPatchedQuery';
import { MESSAGING_UNREAD_COUNTS_QUERY_KEY } from '~queries/messagingQueryKeys';

// Outlasts the mark-read that follows a message in the open conversation, so that mark's own
// re-read joins the one the message's unread event scheduled instead of adding a second request.
export const UNREAD_COUNTS_REREAD_DELAY_MS = 750;

// A steady stream of events would otherwise keep pushing the re-read back indefinitely.
export const UNREAD_COUNTS_REREAD_MAX_WAIT_MS = 2000;

const UNREAD_COUNTS_FILTERS: QueryFilters = {
  queryKey: MESSAGING_UNREAD_COUNTS_QUERY_KEY,
  exact: true,
  type: 'active',
};

async function reReadUnreadCounts(queryClient: QueryClient) {
  const state = queryClient.getQueryState(MESSAGING_UNREAD_COUNTS_QUERY_KEY);
  const isFirstReadInFlight =
    state?.fetchStatus === 'fetching' && state.data === undefined;

  await reReadPatchedQuery(queryClient, UNREAD_COUNTS_FILTERS, isFirstReadInFlight);
}

const scheduledReReads = new WeakMap<QueryClient, DebouncedFunc<() => void>>();

function scheduledReReadFor(queryClient: QueryClient) {
  const existing = scheduledReReads.get(queryClient);
  if (existing) return existing;

  const scheduled = debounce(
    () => void reReadUnreadCounts(queryClient),
    UNREAD_COUNTS_REREAD_DELAY_MS,
    { maxWait: UNREAD_COUNTS_REREAD_MAX_WAIT_MS },
  );
  scheduledReReads.set(queryClient, scheduled);

  return scheduled;
}

export function reReadUnreadCountsSoon(queryClient: QueryClient) {
  scheduledReReadFor(queryClient)();
}
